// SPDX-License-Identifier: MIT
// Schema Panel Component - Tree view of available tables and columns
// Adapted from Kepler.gl's SchemaPanel pattern

import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import type { DefaultTheme } from 'styled-components';
import type { TableSchema } from '../types';

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${props => props.theme.sidePanelBg};
  overflow: hidden;
`;

const Header = styled.div`
  padding: 10px 14px;
  background: ${props => props.theme.panelBackground};
  border-bottom: 1px solid ${props => props.theme.primaryColor}40;
  flex-shrink: 0;
`;

const Title = styled.div`
  color: ${props => props.theme.primaryColor};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
`;

const TableCount = styled.span`
  color: ${props => props.theme.textColor};
  font-size: 11px;
  margin-left: 8px;
  opacity: 0.7;
`;

const TableList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 6px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.borderColor}60;
    border-radius: 4px;
  }
`;

const TableItem = styled.div<{ $isExpanded: boolean }>`
  margin-bottom: 2px;
  border-radius: 4px;
  overflow: hidden;
  background: ${props => (props.$isExpanded ? props.theme.panelBackgroundHover : 'transparent')};
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 10px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${props => props.theme.panelBackgroundHover};
  }
`;

const ExpandIcon = styled.div<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.primaryColor};
  margin-right: 6px;
  transform: ${props => (props.$expanded ? 'rotate(90deg)' : 'rotate(0deg)')};
  transition: transform 0.15s ease;
  opacity: 0.8;
`;

const TableIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 6px;
  opacity: 0.7;
  color: ${props => props.theme.primaryColor};
`;

const TableName = styled.span`
  color: ${props => props.theme.textColor};
  font-size: 13px;
  font-weight: 600;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SecureBadge = styled.span`
  background: #ffd30030;
  color: #ffd300;
  font-size: 10px;
  font-weight: 700;
  padding: 3px 7px;
  border-radius: 3px;
  margin-left: 8px;
  border: 1px solid #ffd30060;
`;

const RowCountBadge = styled.span`
  color: ${props => props.theme.primaryColor};
  font-size: 11px;
  font-weight: 600;
  margin-left: 8px;
  opacity: 0.8;
`;

const ColumnList = styled.div`
  padding: 2px 10px 6px 28px;
`;

const ColumnItem = styled.div`
  display: flex;
  align-items: center;
  padding: 3px 6px;
  font-size: 12px;
  cursor: pointer;
  border-radius: 3px;
  transition: background 0.1s ease;

  &:hover {
    background: ${props => props.theme.panelBackground};
  }
`;

const ColumnName = styled.span`
  color: ${props => props.theme.textColor};
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ColumnType = styled.span`
  color: ${props => props.theme.primaryColor};
  font-size: 10px;
  font-weight: 600;
  margin-left: 8px;
  font-family: 'SF Mono', 'Monaco', 'Menlo', monospace;
  background: ${props => props.theme.primaryColor}15;
  padding: 2px 6px;
  border-radius: 3px;
  border: 1px solid ${props => props.theme.primaryColor}30;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 24px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: 36px;
  margin-bottom: 12px;
  opacity: 0.6;
  color: ${props => props.theme.primaryColor};
`;

const EmptyText = styled.div`
  color: ${props => props.theme.textColor};
  font-size: 13px;
  font-weight: 500;
  line-height: 1.5;
`;

const dotFlashing = keyframes`
  0% { opacity: 0.2; transform: scale(0.8); }
  50% { opacity: 1; transform: scale(1.2); }
  100% { opacity: 0.2; transform: scale(0.8); }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 24px;
`;

const LoadingText = styled.div`
  color: ${props => props.theme.primaryColor};
  font-size: 13px;
  font-weight: 600;
`;

const LoadingDots = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Dot = styled.div<{ $delay: number }>`
  width: 8px;
  height: 8px;
  background: ${props => props.theme.primaryColor};
  border-radius: 50%;
  animation: ${dotFlashing} 1s infinite;
  animation-delay: ${props => props.$delay}s;
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 24px;
  text-align: center;
`;

// Props interface
interface SchemaPanelProps {
  schemas: TableSchema[];
  onTableClick: (tableName: string) => void;
  theme: DefaultTheme;
  isLoading?: boolean;
}

export const SchemaPanel: React.FC<SchemaPanelProps> = ({
  schemas,
  onTableClick,
  theme,
  isLoading = false,
}) => {
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set());

  const toggleTable = (tableName: string) => {
    setExpandedTables(prev => {
      const next = new Set(prev);
      if (next.has(tableName)) {
        next.delete(tableName);
      } else {
        next.add(tableName);
      }
      return next;
    });
  };

  const handleTableDoubleClick = (tableName: string) => {
    onTableClick(tableName);
  };

  const handleColumnClick = (tableName: string, columnName: string) => {
    // Could insert column name into editor
    console.log(`Column clicked: ${tableName}.${columnName}`);
  };

  const formatType = (type: string): string => {
    // Shorten long type names
    const typeMap: Record<string, string> = {
      VARCHAR: 'str',
      INTEGER: 'int',
      BIGINT: 'bigint',
      DOUBLE: 'float',
      BOOLEAN: 'bool',
      TIMESTAMP: 'ts',
      DATE: 'date',
      GEOMETRY: 'geo',
    };
    const upper = type.toUpperCase();
    return typeMap[upper] || type.toLowerCase();
  };

  return (
    <Container theme={theme} data-testid='schema-panel'>
      <Header theme={theme}>
        <Title theme={theme}>
          Tables
          <TableCount theme={theme}>{isLoading ? '(...)' : `(${schemas.length})`}</TableCount>
        </Title>
      </Header>

      {isLoading ? (
        <LoadingState>
          <LoadingContainer>
            <LoadingText theme={theme}>Loading tables</LoadingText>
            <LoadingDots>
              <Dot $delay={0} />
              <Dot $delay={0.15} />
              <Dot $delay={0.3} />
            </LoadingDots>
          </LoadingContainer>
        </LoadingState>
      ) : schemas.length === 0 ? (
        <EmptyState>
          <EmptyIcon>
            <svg viewBox='0 0 24 24' fill='currentColor'>
              <path d='M19,20H4C2.89,20 2,19.1 2,18V6C2,4.89 2.89,4 4,4H10L12,6H19A2,2 0 0,1 21,8H21L4,8V18L6.14,10H23.21L20.93,18.5C20.7,19.37 19.92,20 19,20Z' />
            </svg>
          </EmptyIcon>
          <EmptyText theme={theme}>
            No tables available.
            <br />
            Load data to start querying.
          </EmptyText>
        </EmptyState>
      ) : (
        <TableList theme={theme}>
          {schemas.map(schema => {
            const isExpanded = expandedTables.has(schema.tableName);
            return (
              <TableItem key={schema.tableName} $isExpanded={isExpanded} theme={theme}>
                <TableHeader
                  theme={theme}
                  onClick={() => toggleTable(schema.tableName)}
                  onDoubleClick={() => handleTableDoubleClick(schema.tableName)}
                  title={`Double-click to insert SELECT query`}
                >
                  <ExpandIcon $expanded={isExpanded} theme={theme}>
                    &#9658;
                  </ExpandIcon>
                  <TableIcon>&#128203;</TableIcon>
                  <TableName theme={theme}>{schema.tableName}</TableName>
                  {schema.isSecure && (
                    <SecureBadge theme={theme}>
                      <svg
                        viewBox='0 0 24 24'
                        fill='currentColor'
                        width='10'
                        height='10'
                        style={{ marginRight: '3px' }}
                      >
                        <path d='M12,17A2,2 0 0,0 14,15C14,13.89 13.1,13 12,13A2,2 0 0,0 10,15A2,2 0 0,0 12,17M18,8A2,2 0 0,1 20,10V20A2,2 0 0,1 18,22H6A2,2 0 0,1 4,20V10C4,8.89 4.9,8 6,8H7V6A5,5 0 0,1 12,1A5,5 0 0,1 17,6V8H18M12,3A3,3 0 0,0 9,6V8H15V6A3,3 0 0,0 12,3Z' />
                      </svg>
                      SECURE
                    </SecureBadge>
                  )}
                  <RowCountBadge theme={theme}>{schema.rowCount.toLocaleString()}</RowCountBadge>
                </TableHeader>

                {isExpanded && (
                  <ColumnList>
                    {schema.columns.map(column => (
                      <ColumnItem
                        key={column.name}
                        theme={theme}
                        onClick={() => handleColumnClick(schema.tableName, column.name)}
                        title={`${column.name} (${column.type})`}
                      >
                        <ColumnName theme={theme}>{column.name}</ColumnName>
                        <ColumnType theme={theme}>{formatType(column.type)}</ColumnType>
                      </ColumnItem>
                    ))}
                  </ColumnList>
                )}
              </TableItem>
            );
          })}
        </TableList>
      )}
    </Container>
  );
};

export default SchemaPanel;
